import { z } from 'zod'
import { isValidPhoneNumber } from 'libphonenumber-js'

import { getCurrentAge } from './utils'

/**
 * Local validation error codes
 */
enum ErrorCode {
  Under18 = 'UNDER_18',
  Under16 = 'UNDER_16',
  InvalidDate = 'INVALID_DATE',
}

/**
 * Parse and validate a date string
 *
 * @todo Tidy this [/my] mess up soon
 */
export const IsoDate = z
  .any()
  .refine(
    (date) =>
      typeof date === 'string'
        ? /\d\d\d\d-\d\d?-\d\d?/.test(date)
        : date instanceof Date,
    {
      message: ErrorCode.InvalidDate,
    }
  )
  .transform((val) => {
    const date = new Date(val)
    const year = date.getUTCFullYear()
    const month = `${date.getUTCMonth() + 1}`.padStart(2, '0')
    const day = `${date.getUTCDate()}`.padStart(2, '0')
    return `${year}-${month}-${day}`
  })

/**
 * Validate a trimmed string (ie no whitespace before/after)
 */
export const TrimmedString = (validationMessage: string) =>
  z
    .string({
      required_error: validationMessage,
    })
    .trim()
    .min(1, validationMessage)

/**
 * Validate that a given input is a date representing a year of birth
 * which is over the age of 18
 */
export const Age18 = IsoDate.refine(
  (date) => {
    return date ? getCurrentAge(date) >= 18 : true
  },
  {
    message: ErrorCode.Under18,
  }
)

/**
 * Validate that a given input is a date representing a year of birth
 * which is over the age of 18
 */
export const Age16 = IsoDate.refine(
  (date) => {
    return date ? getCurrentAge(date) >= 16 : true
  },
  {
    message: ErrorCode.Under16,
  }
)

/**
 * Validate that a password meets our expectations
 *
 * @deprecated Use ModernPassword
 */
export const Password = z
  .string()
  .min(8, 'Password must be at least 8 characters long')
  .refine(
    (value) => {
      return !value?.match(/^[a-zA-Z\s\D]+$/)
    },
    { message: 'password must contain a number' }
  )
  .refine(
    (value) => {
      return !value?.match(/^[a-zA-Z\s\d]+$/)
    },
    { message: 'password must contain a special character' }
  )

/**
 * @see https://www.pluralsight.com/blog/security-professional/modern-password-guidelines
 */
export const ModernPasswordString = z
  .string()
  .min(11, 'Password must be at least 11 characters long')
  .max(64, 'Password must be at most 64 characters long')

const phoneNumberError = 'Please enter a valid phone number'

export const ModernPassword = z.preprocess(
  (v) => (typeof v === 'string' ? v.trim() : v),
  ModernPasswordString
)

/**
 * Validate that a phone number validates against E.164 standards. The
 * component will format into E.164.
 */
export const PhoneNumber = (phoneNumber: string) => {
  try {
    const isValid = isValidPhoneNumber(phoneNumber)
    return isValid ? undefined : phoneNumberError
  } catch (error) {
    // number does not seem to be a phone number
    return phoneNumberError
  }
}

/**
 * An environment variable can be declared as a host by passing
 * "gaia.family"
 *
 * @see https://stackoverflow.com/questions/106179/regular-expression-to-match-dns-hostname-or-ip-address
 */
export const Host = z
  .string()
  .regex(
    /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$/
  )

/**
 * @see https://github.com/colinhacks/zod#json-type
 */
const LiteralSchema = z.union([z.string(), z.number(), z.boolean(), z.null()])
type Literal = z.infer<typeof LiteralSchema>
type Json = Literal | { [key: string]: Json } | Json[]
export const JsonSchema: z.ZodType<Json> = z.lazy(() =>
  z.union([LiteralSchema, z.array(JsonSchema), z.record(JsonSchema)])
)

/**
 * Safely parse JSON strings using Zod.
 *
 * @example `z.string().transform().pipe(z.object({ foo: z.string()
 * })).parse('{ "foo": "bar" })')`
 */
export const transformJson: Parameters<z.ZodString['transform']>[0] = (
  json,
  ctx
) => {
  try {
    return JSON.parse(json)
  } catch (e) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      // We know JSON.parse would only return a native error.
      // eslint-disable-next-line no-restricted-globals
      message: (e as Error).message,
    })
    return z.NEVER
  }
}
